// Manage CRM email templates. Mirrors TemplateBuilderPage (SMS), but there is
// no body editor here: each row points at a Brevo transactional template, and
// Brevo owns the HTML, branding and signature. This screen manages the mapping.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaEnvelope, FaEyeSlash,
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import VariableInserter from '../components/VariableInserter';
import { replaceWithSampleData } from '../utils/templateUtils';
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
} from '../../../services/emailTemplateService';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(2)};
  margin-bottom: ${p => p.theme.spacing(3)};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.4rem; }
  svg { color: ${p => p.theme.colors.primary}; font-size: 1.3rem; }
`;

const CreateBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: ${p => p.theme.colors.primary};
  color: #000;
  border: none;
  border-radius: 24px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.9; }

  @media (max-width: 768px) { width: 100%; justify-content: center; }
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(3)};
  border: 1.5px solid ${p => p.theme.colors.primary}33;
`;

const FormTitle = styled.h2`
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing(2.5)} 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const FieldGroup = styled.div`
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray}66;
  font-size: 0.9rem;
`;

const BodyArea = styled.textarea`
  width: 100%;
  min-height: 170px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray}66;
  font-size: 0.9rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
`;

// Renders the default body with sample data, preserving line breaks so the
// author sees the paragraph structure the recipient will get.
const PreviewBox = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  border: 1px dashed ${p => p.theme.colors.primary}55;
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.86rem;
  line-height: 1.5;
  white-space: pre-wrap;

  .preview-label {
    font-size: 0.72rem;
    color: ${p => p.theme.colors.primary};
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray}66;
  font-size: 0.9rem;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 5px;
  line-height: 1.45;

  code {
    background: ${p => p.theme.colors.primary}22;
    color: ${p => p.theme.colors.primary};
    padding: 1px 5px;
    border-radius: 4px;
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.9rem;
  cursor: pointer;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing(2)};
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.88rem;
  background: ${p => (p.variant === 'primary' ? p.theme.colors.primary : p.theme.colors.background)};
  color: ${p => (p.variant === 'primary' ? '#000' : p.theme.colors.textPrimary)};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing(2)};
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(2.5)};
  border: 1px solid ${p => p.theme.colors.inputGray}33;
  opacity: ${p => (p.inactive ? 0.55 : 1)};
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

const CardName = styled.div`
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  word-break: break-word;
`;

const Badge = styled.span`
  font-size: 0.7rem;
  padding: 3px 9px;
  border-radius: 12px;
  background: ${p => p.theme.colors.primary}22;
  color: ${p => p.theme.colors.primary};
  white-space: nowrap;
`;

const CardMeta = styled.div`
  font-size: 0.78rem;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 4px;
  word-break: break-word;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
`;

const CardActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid ${p => p.theme.colors.inputGray}66;
  border-radius: 8px;
  background: transparent;
  color: ${p => (p.danger ? p.theme.colors.error : p.theme.colors.textSecondary)};
  cursor: pointer;
  font-size: 0.78rem;

  &:hover {
    color: ${p => (p.danger ? p.theme.colors.error : p.theme.colors.primary)};
    border-color: ${p => (p.danger ? p.theme.colors.error : p.theme.colors.primary)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing(6)};
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  color: ${p => p.theme.colors.textSecondary};

  svg { font-size: 2rem; color: ${p => p.theme.colors.inputGray}; margin-bottom: 12px; }
`;

const CATEGORIES = ['General', 'Follow-Up', 'Quote', 'Scheduling', 'Thank You'];

const EmailTemplateBuilderPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const [formName, setFormName] = useState('');
  const [formBrevoId, setFormBrevoId] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCategory, setFormCategory] = useState('General');
  const [formActive, setFormActive] = useState(true);
  const [formBody, setFormBody] = useState('');
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // includeInactive so this screen can manage disabled templates too.
      const rows = await getEmailTemplates({ includeInactive: true });
      setTemplates(rows || []);
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to load templates', success: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setFormName('');
    setFormBrevoId('');
    setFormSubject('');
    setFormDescription('');
    setFormCategory('General');
    setFormActive(true);
    setFormBody('');
    setEditingId(null);
  };

  // Insert at the caret so variables land where the author is typing, rather
  // than always at the end.
  const handleInsertVariable = (variable) => {
    const el = bodyRef.current;
    if (!el) {
      setFormBody(prev => prev + variable);
      return;
    }
    const start = el.selectionStart ?? formBody.length;
    const end = el.selectionEnd ?? formBody.length;
    const next = formBody.slice(0, start) + variable + formBody.slice(end);
    setFormBody(next);
    // Restore the caret after React re-renders the controlled textarea.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + variable.length, start + variable.length);
    });
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setFormName(t.name || '');
    setFormBrevoId(String(t.brevo_template_id ?? ''));
    setFormSubject(t.subject || '');
    setFormDescription(t.description || '');
    setFormCategory(t.category || 'General');
    setFormActive(t.is_active !== false);
    setFormBody(t.default_body || '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFeedback({ show: true, message: 'Name is required', success: false });
      return;
    }
    const brevoId = Number(formBrevoId);
    if (!Number.isInteger(brevoId) || brevoId <= 0) {
      setFeedback({ show: true, message: 'Brevo template ID must be a positive number', success: false });
      return;
    }

    const payload = {
      name: formName.trim(),
      brevo_template_id: brevoId,
      subject: formSubject.trim() || null,
      default_body: formBody.trim() || null,
      description: formDescription.trim() || null,
      category: formCategory,
      is_active: formActive,
    };

    try {
      setSaving(true);
      if (editingId) {
        await updateEmailTemplate(editingId, payload);
      } else {
        await createEmailTemplate(payload);
      }
      setFeedback({ show: true, message: editingId ? 'Template updated' : 'Template created', success: true });
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      setFeedback({
        show: true,
        message: err.response?.data?.message || 'Failed to save template',
        success: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    try {
      await deleteEmailTemplate(templateToDelete.id);
      setFeedback({ show: true, message: 'Template deleted', success: true });
      load();
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to delete template', success: false });
    } finally {
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <TitleRow>
          <FaEnvelope />
          <h1>Email Templates</h1>
        </TitleRow>
        <CreateBtn onClick={handleCreate}>
          <FaPlus /> New Template
        </CreateBtn>
      </PageHeader>

      {showForm && (
        <FormCard>
          <FormTitle>{editingId ? 'Edit Template' : 'New Template'}</FormTitle>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-name">Name</FieldLabel>
            <TextInput
              id="tpl-name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Quote Follow-Up"
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-brevo">Brevo template ID</FieldLabel>
            <TextInput
              id="tpl-brevo"
              type="number"
              min="1"
              value={formBrevoId}
              onChange={(e) => setFormBrevoId(e.target.value)}
              placeholder="e.g. 12"
            />
            <Hint>
              The numeric ID of the transactional template in Brevo — that template owns the
              layout, branding and signature. Available merge params:{' '}
              <code>{'{{params.message}}'}</code> <code>{'{{params.first_name}}'}</code>{' '}
              <code>{'{{params.lead_name}}'}</code> <code>{'{{params.city}}'}</code>{' '}
              <code>{'{{params.phone}}'}</code> <code>{'{{params.quote_url}}'}</code>{' '}
              <code>{'{{params.book_a_call_url}}'}</code> <code>{'{{params.estimate_total}}'}</code>{' '}
              <code>{'{{params.company_name}}'}</code>
            </Hint>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-subject">Subject override (optional)</FieldLabel>
            <TextInput
              id="tpl-subject"
              value={formSubject}
              onChange={(e) => setFormSubject(e.target.value)}
              placeholder="Leave blank to use the subject set in Brevo"
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-body">Default message</FieldLabel>
            <BodyArea
              id="tpl-body"
              ref={bodyRef}
              value={formBody}
              onChange={(e) => setFormBody(e.target.value)}
              placeholder={'Hi {first_name},\n\nThanks for reaching out about your driveway.\n\nLet me know if you have any questions.'}
            />
            <Hint>
              Loaded into the composer when someone picks this template, and editable there
              before sending. Line breaks and blank lines are preserved in the email.
            </Hint>
            <VariableInserter onInsert={handleInsertVariable} />
            {formBody.trim() && (
              <PreviewBox>
                <div className="preview-label">Preview (sample data)</div>
                {replaceWithSampleData(formBody)}
              </PreviewBox>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-desc">Description (optional)</FieldLabel>
            <TextInput
              id="tpl-desc"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="When should someone reach for this template?"
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="tpl-cat">Category</FieldLabel>
            <SelectInput
              id="tpl-cat"
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value)}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </SelectInput>
          </FieldGroup>

          <FieldGroup>
            <CheckRow>
              <input
                type="checkbox"
                checked={formActive}
                onChange={(e) => setFormActive(e.target.checked)}
              />
              Active (inactive templates are hidden from the send dialog)
            </CheckRow>
          </FieldGroup>

          <FormActions>
            <Btn onClick={() => { setShowForm(false); resetForm(); }} disabled={saving}>
              <FaTimes /> Cancel
            </Btn>
            <Btn variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <FaSpinner /> : <FaSave />} {saving ? 'Saving…' : 'Save'}
            </Btn>
          </FormActions>
        </FormCard>
      )}

      {loading ? (
        <EmptyState><FaSpinner /><p>Loading templates…</p></EmptyState>
      ) : templates.length === 0 ? (
        <EmptyState>
          <FaEnvelope />
          <p>No email templates yet. Create one in Brevo, then add its ID here.</p>
        </EmptyState>
      ) : (
        <Grid>
          {templates.map(t => (
            <Card key={t.id} inactive={t.is_active === false}>
              <CardTop>
                <CardName>{t.name}</CardName>
                {t.category && <Badge>{t.category}</Badge>}
              </CardTop>
              <CardMeta>Brevo template #{t.brevo_template_id}</CardMeta>
              {t.subject && <CardMeta>Subject: {t.subject}</CardMeta>}
              {t.description && <CardMeta>{t.description}</CardMeta>}
              {t.default_body
                ? <CardMeta>{t.default_body.slice(0, 90)}{t.default_body.length > 90 ? '…' : ''}</CardMeta>
                : <CardMeta><em>No default message — the composer opens empty.</em></CardMeta>}
              {t.is_active === false && (
                <CardMeta><FaEyeSlash /> Inactive</CardMeta>
              )}
              <CardActions>
                <CardActionBtn onClick={() => handleEdit(t)}>
                  <FaEdit /> Edit
                </CardActionBtn>
                <CardActionBtn
                  danger
                  onClick={() => { setTemplateToDelete(t); setDeleteModalOpen(true); }}
                >
                  <FaTrash /> Delete
                </CardActionBtn>
              </CardActions>
            </Card>
          ))}
        </Grid>
      )}

      <Modal
        isOpen={deleteModalOpen}
        title="Delete template"
        message={`Delete "${templateToDelete?.name}"? This only removes it from the CRM — the template in Brevo is untouched.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setTemplateToDelete(null); }}
      />

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback({ ...feedback, show: false })}
      />
    </DashboardLayout>
  );
};

export default EmailTemplateBuilderPage;
